import { useEffect, useMemo, useState } from "react";
import { Flame, PlayCircle, Users, Gift, Trophy, Bird, Cake, Sparkles, Star, Award, Lock, Share2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BadgeShare } from "@/components/BadgeShare";

const ICONS = { flame: Flame, play: PlayCircle, users: Users, gift: Gift, trophy: Trophy, bird: Bird, cake: Cake, sparkles: Sparkles, star: Star };

const BadgeTile = ({ b, onShare }) => {
  const Icon = ICONS[b.icon] || Award;
  const shareable = b.earned && onShare;
  const Tag = shareable ? "button" : "div";
  return (
    <Tag data-testid={`badge-${b.id}`} data-earned={b.earned} title={shareable ? "Abzeichen teilen" : b.description} onClick={shareable ? () => onShare(b) : undefined}
      className={`relative w-full rounded-2xl p-3 text-center border transition-transform duration-200 hover:-translate-y-0.5 ${b.earned ? "bg-gradient-to-b from-rose-50 to-white border-rose-200" : "bg-slate-50 border-slate-100"}`}>
      {shareable && <Share2 className="absolute top-2 right-2 w-3.5 h-3.5 text-rose-500" data-testid={`badge-share-icon-${b.id}`} />}
      <div className={`relative w-11 h-11 mx-auto rounded-full flex items-center justify-center ${b.earned ? "pink-gradient text-white shadow-md shadow-rose-200" : "bg-slate-200 text-slate-400"}`}>
        <Icon className="w-5 h-5" />
        {!b.earned && <Lock className="absolute -bottom-0.5 -right-0.5 w-4 h-4 p-0.5 rounded-full bg-white text-slate-400" />}
      </div>
      <p lang="de" className={`mt-2 text-xs font-bold leading-tight break-words hyphens-auto ${b.earned ? "text-slate-800" : "text-slate-400"}`}>{b.title}</p>
      <p className="text-[10px] text-slate-400 mt-0.5">{b.earned ? b.description : `${b.progress}/${b.target}`}</p>
    </Tag>
  );
};

export function Badges({ userId, isMe }) {
  const { user } = useAuth();
  const [badges, setBadges] = useState(null);
  const [sharing, setSharing] = useState(null);
  const sorted = useMemo(() => [...(badges || [])].sort((a, b) => b.earned - a.earned), [badges]);
  useEffect(() => { setBadges(null); api.get(`/users/${userId}/badges`).then(({ data }) => setBadges(data)).catch(() => setBadges([])); }, [userId]);
  if (!badges?.length) return null;
  const earned = badges.filter((b) => b.earned).length;
  return (
    <div className="bg-white rounded-3xl p-5 sm:p-6 border border-pink-100 space-y-4 fade-up" data-testid="profile-badges">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-lg flex items-center gap-2"><Award className="w-5 h-5 text-rose-500" /> Abzeichen</h3>
        <span className="text-xs font-semibold text-rose-600 bg-rose-50 px-2.5 py-1 rounded-full" data-testid="badges-count">{earned}/{badges.length}</span>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2.5">
        {sorted.map((b) => <BadgeTile key={b.id} b={b} onShare={isMe ? setSharing : null} />)}
      </div>
      {isMe && earned > 0 && <p className="text-xs text-slate-400">Tippe auf ein freigeschaltetes Abzeichen, um es als Bild für Instagram oder WhatsApp zu teilen.</p>}
      {sharing && user && <BadgeShare badge={sharing} Icon={ICONS[sharing.icon] || Award} user={user} onClose={() => setSharing(null)} />}
    </div>
  );
}
